import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Settings, Code, FileText, MessageSquare, Brain, Sparkles, Copy, Check, BookOpen } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AIScenario {
  id: string;
  scenario_code: string;
  scenario_name: string;
  description?: string;
  prompt_template: string;
  model_vendor_id?: string;
  model_name?: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  usage_count?: number;
  created_at: string;
  updated_at: string;
}

const AIScenariosManagement: React.FC = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<AIScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const scenarioTypes = [
    { value: 'deep_insight', label: '深度分析', icon: Brain, color: 'blue' },
    { value: 'content_summary', label: '内容摘要', icon: FileText, color: 'green' },
    { value: 'learning_qa', label: '学习问答', icon: MessageSquare, color: 'purple' },
    { value: 'code_review', label: '代码审查', icon: Code, color: 'orange' },
    { value: 'translation', label: '多语言翻译', icon: Sparkles, color: 'pink' },
    { value: 'knowledge_generation', label: '知识生成', icon: BookOpen, color: 'indigo' }
  ];

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setScenarios(result.data.data || []);
      } else {
        console.error('获取AI场景失败:', result.error);
        toast.error('获取AI场景失败');
      }
    } catch (error) {
      console.error('获取AI场景失败:', error);
      toast.error('获取AI场景失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, scenarioName: string) => {
    if (!confirm(`确定要删除AI场景「${scenarioName}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('AI场景删除成功');
      await fetchScenarios();
    } catch (error) {
      console.error('删除AI场景失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleEdit = (scenario: AIScenario) => {
    navigate(`/admin/scenarios/edit/${scenario.id}`);
  };

  const handleCreate = () => {
    navigate('/admin/scenarios/new');
  };

  const handleCopyContent = async (content: string, id: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      toast.success('内容已复制到剪贴板');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('复制失败');
    }
  };

  const filteredScenarios = scenarios.filter(scenario => {
    const matchesSearch = scenario.scenario_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         scenario.scenario_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (scenario.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getScenarioTypeInfo = (scenarioCode: string) => {
    return scenarioTypes.find(t => t.value === scenarioCode) || {
      value: scenarioCode,
      label: scenarioCode,
      icon: Settings,
      color: 'gray'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Settings className="w-6 h-6 mr-2 text-blue-600" />
              AI场景管理
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加场景
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索场景名称、编码或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* 场景列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredScenarios.map((scenario) => {
              const typeInfo = getScenarioTypeInfo(scenario.scenario_code);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div key={scenario.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-${typeInfo.color}-100`}>
                        <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-600`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{scenario.scenario_name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-800`}>
                            {scenario.scenario_code}
                          </span>
                          {scenario.model_name && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {scenario.model_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCopyContent(scenario.prompt_template, scenario.id)}
                        className="text-gray-400 hover:text-gray-600"
                        title="复制模板"
                      >
                        {copiedId === scenario.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(scenario)}
                        className="text-blue-600 hover:text-blue-900"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(scenario.id, scenario.scenario_name)}
                        className="text-red-600 hover:text-red-900"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {scenario.description && (
                    <p className="text-gray-600 text-sm mb-3">{scenario.description}</p>
                  )}

                  <div className="bg-gray-50 rounded-md p-3 mb-4">
                    <div className="text-xs text-gray-500 mb-1">提示词模板预览</div>
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {scenario.prompt_template.substring(0, 200)}
                      {scenario.prompt_template.length > 200 && '...'}
                    </div>
                  </div>

                  {/* 模型配置信息和使用统计 */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {/* 使用次数 */}
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-700">
                      使用次数: {scenario.usage_count || 0}
                    </span>
                    
                    {scenario.temperature !== undefined && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                        温度: {scenario.temperature}
                      </span>
                    )}
                    {scenario.max_tokens !== undefined && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                        最大Token: {scenario.max_tokens}
                      </span>
                    )}
                    {scenario.top_p !== undefined && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700">
                        Top-P: {scenario.top_p}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>场景编码: {scenario.scenario_code}</span>
                    <span>更新: {new Date(scenario.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredScenarios.length === 0 && (
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无AI场景数据</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIScenariosManagement;